import qs from 'query-string';
import Player from '../player';
import Publisher from '../publisher';
import { PUBLISHER_EVENTS } from '../enums';

function isJsonString(str) {
  try {
    const parseConfig = JSON.parse(str);
    return parseConfig;
  } catch (e) {
    return {};
  }
}

const getOptions = (query, config, onMediaInfo, onTrackInfo) => {
  const parseConfig = isJsonString(config);

  const authTokenName =
    query.authTokenName || parseConfig.authTokenName || 'token';
  const tokenValue = query[authTokenName] || parseConfig[authTokenName] || '';
  const statsSendTime =
    Number(query.statsSendTime) || Number(parseConfig.statsSendTime) || 60;
  const sentryConfig = query.sentryConfig || parseConfig.statsSendTime;
  const inputStreams = window.stream || null;

  return {
    retryMax: 100,
    retryDelay: 3000,
    sentryConfig,
    onMediaInfo,
    onTrackInfo,
    statsSendEnable: true,
    statsSendTime,
    autoplay: true,
    authTokenName,
    tokenValue,
    radeonCheck: true,
    inputStreams,
    constraints: { video: true, audio: true },
  };
};

const getUrl = (query, config) => {
  const parseConfig = isJsonString(config);
  const host = query.host || window.location.hostname;
  const streamName = query.name || parseConfig.streamName;
  if (!streamName || streamName.length < 1 || typeof streamName !== 'string') {
    const { origin, pathname } = window.location;
    return `${origin}${pathname.replace('/embed.html', '')}`;
  }
  return `${
    host.includes('https') || host.includes('http')
      ? ''
      : `${window.location.protocol}//`
  }${host}${
    window.location.port && !query.ignorePort ? `:${window.location.port}` : ''
  }/${streamName}`;
};

const WRTCPlayerLoad = (config = '') => {
  const query = qs.parse(window.location.search);
  const getPlayerElement = () => document.getElementById('webrtc_player');
  const abrInfoElement = document.getElementById('abrInfo');

  let audioTracks = {};
  let videoTracks = {};

  const onMediaInfo = (tracks /* , abr */) => {
    audioTracks = {};
    videoTracks = {};
    tracks.forEach((track) => {
      const { content } = track;
      if (content && content.length) {
        if (content === 'audio') {
          const { track_id, sample_rate } = track;
          audioTracks[track_id] = `${sample_rate} kbps`;
        } else if (content === 'video') {
          const { track_id, params } = track;
          if (params) {
            const { width = '', height = '' } = params;
            videoTracks[track_id] = `${width} x ${height}`;
          } else {
            videoTracks[track_id] = `${0} x ${0}`;
          }
        }
      }
    });
  };

  const onTrackInfo = (trackInfo) => {
    const { audio_track_id, video_track_id } = trackInfo;
    let videoText = '';
    let audioText = '';
    if (video_track_id) {
      videoText = videoTracks[video_track_id] || '';
    }
    if (audio_track_id) {
      audioText = audioTracks[audio_track_id] || '';
    }
    abrInfoElement.innerText = `Auto bitrate set on ${videoText}${
      audioText ? ` with ${audioText} audio` : ''
    }`;
    abrInfoElement.style.opacity = '1';
    setTimeout(() => {
      abrInfoElement.style.opacity = '0';
    }, 3000);
  };

  const playerElement = getPlayerElement();
  playerElement.muted = !(query.mute && query.mute === 'false');

  window.webrtcPlayer = new Player(
    playerElement,
    getUrl(query, config),
    getOptions(query, config, onMediaInfo, onTrackInfo),
    true,
  );
};

// WebRTC Publish
const getPublishElement = () => document.getElementById('publish_button');

const streaming = () => {
  getPublishElement().disabled = true;
};

const startPublish = (config = '') => {
  const query = qs.parse(window.location.search);
  const publisher = new Publisher(
    getUrl(query, config),
    getOptions(query, config),
    true,
  );

  publisher.on(PUBLISHER_EVENTS.STREAMING, streaming);
  window.webrtcPublisher = publisher;
  publisher.start();
};

function gotStream(stream) {
  window.stream = stream; // make stream available to console
  document.getElementById('webrtc_player').srcObject = stream;
  // Refresh button list in case labels have become available
  return navigator.mediaDevices.enumerateDevices();
}

function handleError(error) {
  console.log(
    'navigator.MediaDevices.getUserMedia error: ',
    error.message,
    error.name,
  );
}

const switchOn = (video = true) => {
  const getCam = () => document.getElementById('cam_button');
  const getMic = () => document.getElementById('mic_button');
  if ('stream' in window) {
    const { stream } = window;
    let [track] = stream.getVideoTracks();
    if (!video) {
      [track] = stream.getAudioTracks();
    }
    if (track.enabled) {
      track.enabled = false;
      if (video) {
        getCam().classList.add('off');
      } else {
        getMic().classList.add('off');
      }
    } else {
      track.enabled = true;
      if (video) {
        getCam().classList.remove('off');
      } else {
        getMic().classList.remove('off');
      }
    }
  }
};

const WRTCPublisherLoad = () => {
  const [appBody] = document.getElementsByClassName('app');
  const buttonContainer = document.createElement('div');
  buttonContainer.id = 'webrtc_publish_button_wrapper';
  appBody.appendChild(buttonContainer);
  const publishButton = document.createElement('button');
  publishButton.id = 'publish_button';
  publishButton.classList.add('webrtc_btn');
  publishButton.innerText = 'Publish';
  buttonContainer.appendChild(publishButton);
  const camButton = document.createElement('button');
  camButton.id = 'cam_button';
  camButton.classList.add('webrtc_btn');
  camButton.innerText = 'Cam';
  buttonContainer.appendChild(camButton);
  const micButton = document.createElement('button');
  micButton.id = 'mic_button';
  micButton.classList.add('webrtc_btn');
  micButton.innerText = 'Mic';
  buttonContainer.appendChild(micButton);

  navigator.mediaDevices
    .getUserMedia({ video: true, audio: true })
    .then(gotStream)
    .catch(handleError);

  publishButton.addEventListener('click', startPublish);
  camButton.addEventListener('click', switchOn);
  micButton.addEventListener('click', () => {
    switchOn(false);
  });
};

export { getOptions, getUrl, WRTCPlayerLoad, WRTCPublisherLoad };
